import { useMemo } from "react";

import {
  SteppedMultiColumnDropdown,
  type SteppedMultiColumnDropdownConfig,
  SelectField,
  type SelectFieldProps,
} from "@stacklet/ui";

/** Props for JiraWorkType component */
interface Props extends Pick<
  SelectFieldProps,
  "className" | "aria-label" | "isDisabled"
> {
  /** Available work types from the selected Jira space */
  workTypes: {
    name: string;
    iconUrl: string;
  }[];
  /** Currently selected work type name (if any) */
  selectedWorkTypeName?: string;
  /**
   * When true, show a loading label instead of "No Types Available".
   * Useful in edit mode while space/workTypes are being derived.
   */
  isLoading?: boolean;
  /** Callback when work type selection changes */
  onChange: (selections: string[]) => void;
}

export function JiraWorkType({
  workTypes,
  selectedWorkTypeName,
  isLoading = false,
  onChange,
  isDisabled = false,
  className = "w-full",
}: Props) {
  // Transform work types to dropdown items
  const workTypeItems = useMemo(
    () =>
      workTypes.map((workType) => ({
        id: workType.name,
        name: workType.name,
      })),
    [workTypes],
  );

  // Single column configuration with dynamic items
  const config: SteppedMultiColumnDropdownConfig = useMemo(
    () => [
      [
        {
          type: "list",
          title: "Select Type",
          items: workTypeItems,
          flex: true,
        },
      ],
    ],
    [workTypeItems],
  );

  // Display selected value or placeholder in trigger
  const triggerText = useMemo(() => {
    if (isDisabled) {
      return "Jira Space Required";
    }
    if (selectedWorkTypeName) {
      return selectedWorkTypeName;
    }
    if (isLoading) {
      return "Loading types…";
    }
    if (workTypeItems.length === 0) {
      return "No Types Available";
    }
    return "Select Type";
  }, [isDisabled, isLoading, workTypeItems, selectedWorkTypeName]);

  const trigger = ({ isOpen }: { isOpen: boolean }) => (
    <SelectField
      className={className}
      isDisabled={isDisabled}
      isOpen={isOpen}
      value={triggerText}
    />
  );

  return (
    <SteppedMultiColumnDropdown
      className="w-full"
      config={config}
      initialSelections={
        selectedWorkTypeName ? [selectedWorkTypeName] : undefined
      }
      onChange={onChange}
      trigger={trigger}
      autoHeight
    />
  );
}
